import {
  Card,
  Checkbox,
  Field,
  Textarea,
  useToastContext,
} from '@payaca/ambrosia';
import { FC, useCallback, useEffect, useMemo, useState, type JSX } from 'react';

import purchaseOrderKeys from '@/api/queries/purchase-orders/keyFactory';
import { useSelector } from '@/api/state';
import { getRegion } from '@/utils/stateAccessors';
import BasicField, {
  LabelPositionVariant,
} from '@payaca/components/basicField/BasicField';
import Button from '@payaca/components/button/Button';
import { ButtonColourVariant } from '@payaca/components/button/enums';
import IconButton from '@payaca/components/button/IconButton';
import ImageBlock from '@payaca/components/imageBlock/ImageBlock';
import { InputWidth } from '@payaca/components/inputWrapper/InputWrapper';

import { EmptyState } from '@payaca/ambrosia';
import ValidatedForm from '@payaca/components/validatedForm/ValidatedForm';
import { currencyPrice } from '@payaca/helpers/financeHelper';
import { updateIsolatedPurchaseOrder } from '@payaca/store/materialsList/materialsListActions';
import { UpdateIsolatedPurchaseOrderRequestData } from '@payaca/store/materialsList/materialsListTypes';
import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';
import {
  HydratedMaterialPurchaseIntent,
  HydratedPurchaseOrder,
  MaterialPurchaseIntent,
} from '@payaca/types/materialsListTypes';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import BuildMaterialsListSidebar from '../buildMaterialsListSidebar/BuildMaterialsListSidebar';
import { DateInput } from '../dateInput/DateInput';
import { IsolatedPurchaseOrderDeliveryAddressControl } from '../purchaseOrderDeliveryAddressControl/IsolatedPurchaseOrderDeliveryAddressControl';

type Props = {
  purchaseOrder: HydratedPurchaseOrder;
  onPreviewPurchaseOrderClick?: () => void;
};

const IsolatedPurchaseOrderControl: FC<Props> = ({
  purchaseOrder,
  onPreviewPurchaseOrderClick,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const { pushSuccess, pushError } = useToastContext();

  const [updatePurchaseOrder, setUpdatedPurchaseOrder] =
    useState(purchaseOrder);

  const queryClient = useQueryClient();

  const initialFormState = useMemo(() => {
    return {
      additionalNotes: purchaseOrder.additionalNotes,
      materialPurchaseIntents: purchaseOrder.materialPurchaseIntents.map(
        (mpi) => {
          return {
            id: mpi.id,
            materialQuantity: mpi.materialQuantity,
          };
        }
      ),
      dueAt: purchaseOrder.dueAt ? new Date(purchaseOrder.dueAt) : undefined,
      deliveryAddressId: purchaseOrder.deliveryAddressId || undefined,
      showMaterialPrices: purchaseOrder.showMaterialPrices || false,
    };
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has missing dependencies: 'purchaseOrder.materialPurchaseIntents', 'purchaseOrder.showMaterialPrices', 'purchaseOrder.additionalNotes', 'purchaseOrder.dueAt', and 'purchaseOrder.deliveryAddressId'
  }, []);

  const onSubmit = useCallback(
    (
      isValid: boolean,
      updateData: UpdateIsolatedPurchaseOrderRequestData,
      callback?: (purchaseOrder: HydratedPurchaseOrder) => void
    ) => {
      dispatch(
        updateIsolatedPurchaseOrder.request({
          payload: {
            ...updateData,
            purchaseOrderId: purchaseOrder.id,
          },
          callback: (purchaseOrder: HydratedPurchaseOrder) => {
            // invalidate query if deliveryaddressid was passed
            if (updateData.deliveryAddressId) {
              void queryClient.invalidateQueries({
                queryKey: purchaseOrderKeys.purchaseOrderAddress(
                  purchaseOrder.id
                ),
              });
            }
            pushSuccess('Your changes have been saved');
            setUpdatedPurchaseOrder(purchaseOrder);
            callback?.(purchaseOrder);
          },
          onErrorCallback: () => {
            pushError(`We couldn't save your changes`);
          },
        })
      );
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'dispatch', 'purchaseOrder.id', 'pushSuccess', 'pushError', and 'queryClient'
    []
  );

  return (
    <>
      <ValidatedForm<UpdateIsolatedPurchaseOrderRequestData>
        initialFormState={initialFormState}
        fieldValidators={{}}
        renderFormContents={(
          isValid,
          formState,
          validationState,
          touchedState,
          onFieldChange,
          onFieldTouch
        ) => (
          <FormContents
            isValid={isValid}
            formState={formState}
            validationState={validationState}
            touchedState={touchedState}
            onFieldChange={onFieldChange}
            onFieldTouch={onFieldTouch}
            onSubmit={onSubmit}
            isSubmitting={false}
            purchaseOrder={updatePurchaseOrder}
            onPreviewPurchaseOrderClick={onPreviewPurchaseOrderClick}
          />
        )}
      />
    </>
  );
};

const FormContents: FC<{
  isValid: boolean;
  formState: UpdateIsolatedPurchaseOrderRequestData;
  validationState: {
    [key: string]: FieldValidationResult;
  };
  touchedState: {
    [key: string]: boolean;
  };
  onFieldChange: (value: { [key: string]: any }) => void;
  onFieldTouch: (fieldName: string) => void;
  onSubmit: (
    isValid: boolean,
    formState: UpdateIsolatedPurchaseOrderRequestData,
    callback?: (purchaseOrder: HydratedPurchaseOrder) => void
  ) => void;
  isSubmitting: boolean;
  errorMessage?: string;
  purchaseOrder: HydratedPurchaseOrder;
  onPreviewPurchaseOrderClick?: () => void;
}> = ({
  isValid,
  formState,
  validationState,
  touchedState,
  onFieldChange,
  onFieldTouch,
  onSubmit,
  isSubmitting,
  errorMessage,
  purchaseOrder,
  onPreviewPurchaseOrderClick,
}) => {
  const [showSearchMaterialsDrawer, setShowSearchMaterialsDrawer] =
    useState(false);
  const [requiresSave, setRequiresSave] = useState(false);

  const region = useSelector(getRegion);

  useEffect(() => {
    if (requiresSave) {
      setRequiresSave(false);
      onSubmit(isValid, formState);
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'isValid', 'onSubmit', and 'formState'
  }, [requiresSave]);

  const { totalMaterialQuantity, predictedTotalPriceExcludingTax } =
    useMemo(() => {
      return purchaseOrder.materialPurchaseIntents.reduce(
        (
          acc: {
            totalMaterialQuantity: number;
            predictedTotalPriceExcludingTax: number;
          },
          item
        ) => {
          acc.totalMaterialQuantity += item.materialQuantity;
          acc.predictedTotalPriceExcludingTax +=
            item.predictedTotalPriceExcludingTax;
          return acc;
        },
        {
          totalMaterialQuantity: 0,
          predictedTotalPriceExcludingTax: 0,
        }
      );
    }, [purchaseOrder.materialPurchaseIntents]);

  const existingMaterialIds = purchaseOrder.materialPurchaseIntents.map(
    (mpi: HydratedMaterialPurchaseIntent) => mpi.material.publicId
  );

  return (
    <div className="flex flex-col items-start gap-8">
      <div className="flex flex-wrap gap-8">
        <div className="min-w-[30rem] max-w-[80rem] shrink grow-[2] basis-0">
          <Card>
            <Card.Body>
              <div className="flex gap-4">
                <div className="shrink grow">
                  <Card.Title>Materials required</Card.Title>
                  <p className="my-4 text-base">
                    Browse existing materials linked with{' '}
                    {purchaseOrder.supplier.name} or add new ones
                  </p>
                </div>
                <div className="shrink-0">
                  <Button
                    iconAfter="plus.3"
                    onClick={() => setShowSearchMaterialsDrawer(true)}
                    isOutlined={!!purchaseOrder.materialPurchaseIntents.length}
                  >
                    Add materials
                  </Button>
                </div>
              </div>
              {!formState.materialPurchaseIntents?.length && (
                <EmptyState text="Nothing to see here yet" />
              )}
              <ul className="m-0 list-none p-0">
                {formState.materialPurchaseIntents?.map((mpi) => {
                  const hydratedMpi =
                    purchaseOrder.materialPurchaseIntents.find(
                      (x) => x.id === mpi.id
                    );

                  if (!hydratedMpi) return;

                  return (
                    <li
                      key={`mpi-${mpi.id}`}
                      className="border-b py-2.5 last:border-b-0"
                    >
                      <MaterialPurchaseIntentControl
                        materialPurchaseIntent={{
                          ...hydratedMpi,
                          materialQuantity: mpi.materialQuantity,
                        }}
                        onRemove={() => {
                          const newMpis =
                            formState.materialPurchaseIntents.filter(
                              (mpi2) => mpi2.id !== mpi.id
                            );
                          onFieldChange({
                            materialPurchaseIntents: newMpis,
                          });
                          setRequiresSave(true);
                        }}
                        onQuantityChange={(materialQuantity) => {
                          const newMpis = formState.materialPurchaseIntents.map(
                            (mpi2) =>
                              mpi2.id !== mpi.id
                                ? mpi2
                                : { ...mpi2, materialQuantity }
                          );
                          onFieldChange({
                            materialPurchaseIntents: newMpis,
                          });
                        }}
                        onQuantityChangeTimeout={() => {
                          setRequiresSave(true);
                        }}
                      />
                    </li>
                  );
                })}
              </ul>
              {!!totalMaterialQuantity && (
                <div className="flex">
                  <div className="mt-4 flex w-auto flex-col items-end text-base">
                    <div>
                      Predicted cost ({totalMaterialQuantity} items):{' '}
                      <strong>
                        {currencyPrice(predictedTotalPriceExcludingTax, region)}
                      </strong>
                    </div>
                    <div className="text-gray-500">Exc tax</div>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
        <div className="min-w-[30rem] max-w-[80rem] shrink grow-[1] basis-0">
          <Card>
            <Card.Body>
              <div className="flex flex-col gap-4">
                <Field.Legacy name="dueAt">
                  <Field.Label>Due by</Field.Label>
                  <DateInput
                    value={formState.dueAt}
                    onChange={(value) => {
                      onFieldChange({ dueAt: value });
                      onFieldTouch('dueAt');
                      setRequiresSave(true);
                    }}
                  />
                </Field.Legacy>

                <Field.Legacy name="deliveryAddressId">
                  <Field.Label>Deliver to</Field.Label>
                  <IsolatedPurchaseOrderDeliveryAddressControl
                    purchaseOrderId={purchaseOrder.id}
                    supplierId={purchaseOrder?.supplierId}
                    onChange={(value) => {
                      onFieldChange(value);
                      setRequiresSave(true);
                    }}
                    value={{
                      deliveryAddressId: formState.deliveryAddressId,
                    }}
                  />
                </Field.Legacy>

                <Field.Legacy name="additionalNotes">
                  <Field.Label>Additional notes</Field.Label>
                  <Textarea
                    onChange={(value) => {
                      onFieldChange({ additionalNotes: value });
                    }}
                    onTouch={() => onFieldTouch('additionalNotes')}
                    value={formState.additionalNotes || undefined}
                    onChangeTimeout={() => setRequiresSave(true)}
                  />
                </Field.Legacy>
                <Field.Legacy name="showMaterialPrices">
                  <Checkbox
                    checked={formState.showMaterialPrices}
                    onChange={() => {
                      onFieldChange({
                        showMaterialPrices: !formState.showMaterialPrices,
                      });
                      setRequiresSave(true);
                    }}
                    label="Show Material prices on Purchase Order"
                  />
                </Field.Legacy>
              </div>
            </Card.Body>
          </Card>
        </div>
        <BuildMaterialsListSidebar
          defaultFilters={{
            suppliers: [purchaseOrder.supplier.id.toString()],
          }}
          isOpen={showSearchMaterialsDrawer}
          onClose={() => setShowSearchMaterialsDrawer(false)}
          disabledMaterialIds={existingMaterialIds}
          onAddMaterials={async (materials) => {
            const existingMpis = formState.materialPurchaseIntents;

            const newMpis = materials
              .filter((material) => !existingMaterialIds.includes(material.id))
              .map((material) => {
                return {
                  materialId: Number(material.internalId),
                  materialQuantity: material.quantity,
                };
              });

            return new Promise((resolve) => {
              onSubmit(
                isValid,
                {
                  ...formState,
                  materialPurchaseIntents: [...existingMpis, ...newMpis],
                },
                (po) => {
                  onFieldChange({
                    materialPurchaseIntents: po.materialPurchaseIntents.map(
                      (mpi) => {
                        return {
                          id: mpi.id,
                          materialQuantity: mpi.materialQuantity,
                        };
                      }
                    ),
                  });
                  resolve();
                }
              );

              setShowSearchMaterialsDrawer(false);
            });
          }}
        />
      </div>
      {onPreviewPurchaseOrderClick && !!totalMaterialQuantity && (
        <Button onClick={onPreviewPurchaseOrderClick}>
          Preview Purchase Order
        </Button>
      )}
    </div>
  );
};

const MaterialPurchaseIntentControl: FC<{
  materialPurchaseIntent: HydratedMaterialPurchaseIntent;
  onQuantityChange: (
    materialQuantity: MaterialPurchaseIntent['materialQuantity']
  ) => void;
  onQuantityChangeTimeout: () => void;
  onRemove: () => void;
}> = ({
  materialPurchaseIntent,
  onQuantityChange,
  onRemove,
  onQuantityChangeTimeout,
}) => {
  const region = useSelector(getRegion);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="shrink-0">
        <ImageBlock
          imageSrc={materialPurchaseIntent.material.thumbnailUrl}
          renderBlockIfNoImageSrc={true}
        />
      </div>
      <span className="grow font-semibold">
        {materialPurchaseIntent.material.name}
      </span>
      <div className="ml-auto flex shrink-0 grow-0 items-center gap-2">
        <div className="shrink-0 grow-0">
          <BasicField
            label="Qty"
            name="materialQuantity"
            onChange={(value) => {
              onQuantityChange(+value.materialQuantity);
            }}
            inputWidth={InputWidth.XXSM}
            type="numeric"
            additionalInputProps={{
              step: 1,
              min: 0,
              defaultValue: materialPurchaseIntent.materialQuantity,
            }}
            labelPosition={LabelPositionVariant.INLINE}
            onChangeTimeout={onQuantityChangeTimeout}
            changeTimeoutMs={500}
          />
        </div>
        <span className="w-[5rem] shrink-0 grow-0">
          {currencyPrice(
            materialPurchaseIntent.predictedUnitPriceExcludingTax,
            region
          )}
        </span>
        <div>
          <IconButton
            icon="x.3"
            onClick={onRemove}
            size={'sm'}
            colourVariant={ButtonColourVariant.WHITE}
          />
        </div>
      </div>
    </div>
  );
};

export default IsolatedPurchaseOrderControl;
